import { useReducer, useEffect } from 'react';

const { getAuthenticatedHttpClient } = require('@edx/frontend-platform/auth');
const { getConfig } = require('@edx/frontend-platform');

const initialState = {
  courses: 0,
  instructors: 0,
  learners: 0,
  videos: 0,
  problems: 0,
  certificates: 0,
};

interface Data {
  courses: number;
  instructors: number;
  learners: number;
  components: {
    problem?: number;
    video?: number;
  },
  certificates: {
    downloadable?: number;
  }
}

interface State {
  courses: number;
  instructors: number;
  learners: number;
  videos: number;
  problems: number;
  certificates: number;
}

interface Action {
  type: string;
  state: State;
}

interface OnSuccess {
  (data?: Data): void;
}

interface OnFailure {
  (error?: Error): void;
}

/**
 * Function that specifies how the state gets updated.
 * @param  {State} state   Current state.
 * @param  {Action} action Describes kind of action to be executed.
 * @return {Object}        Updated state based on the action type.
 */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_TENANT_STATS':
      return action.state;
    default:
      return initialState ?? state;
  }
}

/**
 * This makes a GET request and allow to execute a function in case on success or on failure.
 * @param  {String}       courseID   Course identifier as string.
 * @param  {OnSuccess}    onSuccess  Function that will be executed on 20X responses.
 * @param  {OnFailure}    onFailure  Function that will be executed on responses different from 20X responses.
 */
function getTenantStats(onSuccess: OnSuccess, onFailure: OnFailure) {
  const { STATS_SETTINGS } = getConfig();
  const url = `${STATS_SETTINGS?.API_BASE_URL}/tenant/`;
  const apiConfig = { headers: { accept: 'application/vnd.api+json,application/json' } };

  getAuthenticatedHttpClient().get(url, apiConfig).then(({ data }: { data: Data }) => {
    onSuccess(data);
  }, (error: Error) => {
    onFailure(error);
  });
}

/**
 * Custom hook that retrieves current tenant stats.
 *
 * @returns {State}   state    Returns stats.
 */
function useTenantStats(): State {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const getOnSuccess = (data?: Data) => {
      // On success the state will be updated based on the API response.
      const learners = data?.learners ?? 0;
      const instructors = data?.instructors ?? 0;
      const courses = data?.courses ?? 0;
      const videos = data?.components?.video ?? 0;
      const problems = data?.components?.problem ?? 0;
      const certificates = data?.certificates?.downloadable ?? 0;

      dispatch({
        type: 'SET_TENANT_STATS',
        state: {
          courses, instructors, learners, problems, videos, certificates,
        },
      });
    };
    const getOnFailure = (error: Error) => {
      // On failure the error will be logged on the browser console and the state will be set to the default value.
      // eslint-disable-next-line no-console
      console.error('Unable to retrive tenant stats data with error:', error.message);
      dispatch({ type: 'DEFAULT', state });
    };
    getTenantStats(getOnSuccess, getOnFailure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

export default useTenantStats;
